import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Form,
  Input,
  Select,
  Radio,
  message,
  Typography,
  Row,
  Col,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams, useLocation } from "react-router";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import CardFooterAction from "../../components/CardFooterAction";
import pallete from "../../utils/pallete";
import { useGetDetailApi, useStoreUpdateApi } from "../../hooks/useApi";
import { vocationalInstitutionService } from "../../services/vocationalInstitution.service";
import { institutionalTypeService } from "../../services/institutionalType.service";
import CustomTextInput from "../../components/CustomTextInput";
import dayjs from "dayjs";

const { Option } = Select;
const center = [-6.2088, 106.8456]; // Jakarta

// Atur ikon default Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Komponen marker + event click pada peta
function LocationMarker({ position, setPosition, setAddress, form }) {
  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      reverseGeocode(lat, lng, setAddress, form);
    },
  });
  return position ? <Marker position={position} /> : null;
}

// Reverse geocoding Nominatim (koordinat → alamat)
const reverseGeocode = async (lat, lng, setAddr, form) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await res.json();
    if (data?.display_name) {
      setAddr(data.display_name);
      form.setFieldsValue({ address: data.display_name });
    }
  } catch (err) {
    console.error("Reverse geocode error:", err);
  }
};

export default function VocationalInstitutionForm() {
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedLocation, setSelectedLocation] = useState(center);
  const [address, setAddress] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [kabupatens, setKabupatens] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);

  const { data: institutionalTypes } = useQuery({
    queryFn: () => institutionalTypeService.fetchAllWithoutPaginate(),
  });

  const { data } = useGetDetailApi({
    id,
    serviceFn: vocationalInstitutionService.findById,
    queryKey: ["vocational-institution"],
  });

  const { storeMutation, updateMutation } = useStoreUpdateApi({
    service: vocationalInstitutionService,
    form: form,
    messageApi: messageApi,
    queryKey: "vocational-institution",
    route: "/vocational-institution",
  });

  // Fetch provinsi saat mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          "https://ibnux.github.io/data-indonesia/provinsi.json"
        );
        setProvinces(await res.json());
      } catch {
        message.error("Gagal memuat data provinsi");
      }
    })();
  }, []);

  // Fetch kabupaten
  const fetchKabupatens = async (provId) => {
    try {
      const res = await fetch(
        `https://ibnux.github.io/data-indonesia/kabupaten/${provId}.json`
      );
      setKabupatens(await res.json());
      return res.json();
    } catch {
      message.error("Gagal memuat data kabupaten");
    }
  };

  // Saat edit data (mode update)
  useEffect(() => {
    if (!id) return;
    if (!data) return;

    const accreditationEndDate =
      data.accreditationEndDate || data.legality?.accreditationEndDate;

    form.setFieldsValue({
      ...data,
      ...(accreditationEndDate && {
        accreditationEndDate: dayjs(accreditationEndDate),
      }),
      ...(data.tanggal_input && {
        tanggal_input: dayjs(data.tanggal_input),
      }),
    });
    if (data.lat && data.long) setSelectedLocation([data.lat, data.long]);
    if (data.address) setAddress(data.address);

    if (data.province) {
      setSelectedProvince(data.province);
      fetchKabupatens(data.province);
    }
  }, [id, data, form]);

  useEffect(() => {
    form.setFieldsValue({
      lat: selectedLocation[0],
      long: selectedLocation[1],
    });
  }, [selectedLocation]);

  // Handle ubah provinsi
  const handleProvinceChange = (provId) => {
    const prov = provinces.find((p) => p.id === provId);
    if (!prov) return;
    setSelectedProvince(provId);
    setKabupatens([]);
    form.setFieldsValue({ province: prov.nama, district: undefined });
    fetchKabupatens(provId);
  };

  // Handler ubah alamat manual
  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    form.setFieldsValue({ address: value });
  };

  // Simulasi simpan & update (ganti dengan service API nanti)
  const handleMutation = useMutation({
    mutationFn: (payload) =>
      new Promise((res) =>
        setTimeout(
          () =>
            res({
              ...payload,
              lat: selectedLocation[0],
              lng: selectedLocation[1],
            }),
          1000
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["lembagaVokasi"]);
      message.success(`Data berhasil ${id ? "diubah" : "disimpan"}`);
      form.resetFields();
      navigate("/lembaga-vokasi");
    },
    onError: () => message.error("Terjadi kesalahan"),
  });

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      accreditationEndDate: values.accreditationEndDate
        ? dayjs(values.accreditationEndDate).format("YYYY-MM-DD")
        : null,
      ...(values.tanggal_input && {
        tanggal_input: dayjs(values.tanggal_input).format("YYYY-MM-DD"),
      }),
    };
    if (!id) {
      storeMutation.mutate(payload);
    } else {
      updateMutation.mutate({ id, data: payload });
    }
  };

  const isPending = storeMutation.isPending || updateMutation.isPending;

  return (
    <>
      {contextHolder}
      <Card
        title={`${id ? "Ubah" : "Tambah"} Lembaga Vokasi`}
        actions={[
          <CardFooterAction
            key="1"
            route="/vocational-institution"
            onClick={() => form.submit()}
            loading={handleMutation.isPending}
            text="Simpan"
          />,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* === Data Akun === */}
          <SectionTitle title="Data Akun" />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email harus diisi" },
                  { type: "email", message: "Email tidak valid" },
                ]}
                disabled={isPending}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Password"
                name={id ? "newPassword" : "password"}
                rules={
                  !id
                    ? [{ required: true, message: "Password wajib diisi" }]
                    : []
                }
                password
                disabled={isPending}
              />
            </Col>
          </Row>

          {/* === Data Legalitas === */}
          <SectionTitle title="Data Legalitas" />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="ID Lembaga"
                name="institutionId"
                rules={[{ required: true, message: "ID Lembaga harus diisi" }]}
                disabled={isPending}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Nama Lembaga"
                name="name"
                rules={[
                  { required: true, message: "Nama Lembaga harus diisi" },
                ]}
                disabled={isPending}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    Status Legalitas
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                name="status"
              >
                <Select size="large">
                  <Option value="Negeri">Negeri</Option>
                  <Option value="Swasta">Swasta</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Nomor Izin Operasional"
                name="operationalLicenseNumber"
                rules={[
                  {
                    required: true,
                    message: "Nomor Izin Operasional harus diisi",
                  },
                ]}
                disabled={isPending}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Nama Pimpinan"
                name="leaderName"
                rules={[
                  {
                    required: true,
                    message: "Nama Pimpinan harus diisi",
                  },
                ]}
                disabled={isPending}
              />
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    Jenis Lembaga
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                name="typeId"
              >
                <Select
                  size="large"
                  options={institutionalTypes?.data?.map((e) => ({
                    label: e.name,
                    value: e.uuid,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    Bentuk Lembaga
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                name="institutionForm"
              >
                <Select size="large">
                  <Option value="Pelatihan">Pelatihan</Option>
                  <Option value="Pendidikan & Pelatihan">
                    Pendidikan & Pelatihan
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Tanggal Berakhir Akreditasi"
                name="accreditationEndDate"
                date
                starLabel={false}
                disabled={isPending}
              />
            </Col>
          </Row>

          {/* === Data Kontak === */}
          <SectionTitle title="Data Kontak" />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Nomor Telepon"
                name="phone"
                rules={[
                  { required: true, message: "Nomor Telepon harus diisi" },
                ]}
                disabled={isPending}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Website Lembaga"
                name="website"
                rules={[
                  { required: true, message: "Website Lembaga harus diisi" },
                ]}
                disabled={isPending}
              />
            </Col>
          </Row>

          {/* === Alamat & Peta === */}
          <SectionTitle title="Data Alamat" />
          <Form.Item
            label={
              <Typography.Text>
                Alamat Lembaga
                <Typography.Text style={{ color: "#940808" }}>
                  *
                </Typography.Text>
              </Typography.Text>
            }
            required={false}
            name="address"
            rules={[{ required: true, message: "Alamat Lembaga wajib diisi" }]}
          >
            <Input
              style={{
                fontSize: 14,
              }}
              size="large"
              value={address}
              onChange={handleAddressChange}
            />
          </Form.Item>

          <div style={{ height: 200, marginBottom: 16 }}>
            <MapContainer
              center={selectedLocation}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <LocationMarker
                position={selectedLocation}
                setPosition={setSelectedLocation}
                setAddress={setAddress}
                form={form}
              />
            </MapContainer>
          </div>

          <Form.Item hidden name={"lat"}>
            <Input />
          </Form.Item>

          <Form.Item hidden name={"long"}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    Provinsi
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                name="province"
              >
                <Select onChange={handleProvinceChange} size="large">
                  {provinces.map((prov) => (
                    <Option key={prov.id} value={prov.id}>
                      {prov.nama}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    Kabupaten
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                name="district"
              >
                <Select
                  disabled={!selectedProvince}
                  size="large"
                  onChange={(kabId) => {
                    const kab = kabupatens.find((k) => k.id === kabId);
                    form.setFieldsValue({ district: kab?.nama });
                  }}
                >
                  {kabupatens.map((kab) => (
                    <Option key={kab.id} value={kab.id}>
                      {kab.nama}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Data Lainnya */}
          <SectionTitle title="Data Lainnya" />
          <CustomTextInput
            label="Tanggal Input"
            name="tanggal_input"
            date
            disabled={isPending}
          />

          {/* === Status === */}
          <Form.Item
            label={
              <Typography.Text>
                Status
                <Typography.Text style={{ color: "#940808" }}>
                  *
                </Typography.Text>
              </Typography.Text>
            }
            required={false}
            name="isActive"
          >
            <Radio.Group>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}

// Komponen kecil untuk judul bagian
const SectionTitle = ({ title }) => (
  <Typography.Title
    level={5}
    style={{ color: pallete.background.black, marginTop: 20 }}
  >
    {title}
  </Typography.Title>
);
